/**
 * `GET /venue/v1/manager/venues` 응답의 소비 타입 — **#584 §1 계약의 사본**, 이용권 폐지 이후
 * 운영 인증 축으로 개정(`#761` §2 · `#747` §1).
 *
 * 🚨 서버가 계약을 바꾸지 않는 한 여기서 필드를 파생하거나 이름을 바꾸지 않는다. 모든 상태는
 * `baseTime` 기준 **서버 파생값**이며 클라이언트 시계로 다시 계산하지 않는다(만료 경계가 어긋난다).
 */

export type VenueType = 'HOSTED' | 'PUBLIC'

export type VenueReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

export type VenueExposureStatus = 'EXPOSED' | 'HIDDEN'

export type VenueOperationVerificationStatus = 'UNVERIFIED' | 'VERIFIED' | 'EXPIRED'

/**
 * 운영 인증 축(`#747` §1) — 구 등록 이용권을 대체한다. 사용자 노출 판정(`APPROVED ∧ EXPOSED ∧
 * operationVerifiedUntil > now`)의 한 축이며, 이 값 자체를 클라이언트가 재계산하지 않는다.
 */
export interface VenueOperationVerification {
  status: VenueOperationVerificationStatus
  statusLabel: string
  /** 인증 유효 기한(ISO-8601 UTC). 미인증이면 null. */
  verifiedUntil: string | null
  /** 🚨 만료·미인증이면 null(음수를 내려보내지 않는다). */
  remainingDays: number | null
}

export interface ManagedVenueItem {
  venueId: string
  name: string
  venueType: VenueType
  facilityType: string
  reviewStatus: VenueReviewStatus
  exposureStatus: VenueExposureStatus
  /** 승인 ∧ 노출 중 ∧ 운영 인증 유효 — 🚨 '노출 중' 배지의 **단일 출처**. 재계산 금지 */
  userVisible: boolean
  operationVerification: VenueOperationVerification
  /** 구 예약 시스템 이용권 축을 대체 — 예약 기능 사용 설정 화면(`reservation-setting`)의 ON/OFF 값과 동형. */
  reservationEnabled: boolean
  /** ⚠️ 등록 신청서 스냅샷이다(코트 테이블 집계가 아니다) */
  courtCount: number
  stateId: string
  cityId: string
  address: string
}

export interface ManagedVenueListView {
  /** 🚨 이 응답의 모든 상태 판정 기준시각 */
  baseTime: string
  /** 만료 임박 창(일). 🚨 화면이 D-7 을 하드코딩하지 말 것 */
  expiringWithinDays: number
  /** `venueId` 오름차순 고정. 🚨 연결 구장 0건은 에러가 아니라 빈 배열이다(#584 §1) */
  venues: ManagedVenueItem[]
}

/** 목록에서 특정 구장을 찾는다. 없으면 `undefined`(= 내가 관리하지 않는 구장). */
export function findManagedVenue(
  view: ManagedVenueListView | undefined,
  venueId: string
): ManagedVenueItem | undefined {
  return view?.venues.find((venue) => venue.venueId === venueId)
}
